/**
 * Builds and updates the Postgres search payload for external PA orders from filters and search text.
 */
import { useEffect, useRef, useState } from "react";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { FilterValues } from "../../../../../data-model/filterValues";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { generateFilterPayloadForExternalOrders } from "../utils/generateFilterPayloadForExternalOrders";

/**
 * Generates and maintains the Postgres search payload from active filters and search term.
 * Defers payload generation until after the initial data fetch completes, then recalculates
 * whenever filters, search term, or org ID change. Uses a single effect with a ref guard
 * so the payload is built immediately once the initial fetch finishes.
 *
 * @param validFilters - Currently active filter values for the external PA orders table
 * @param searchTerm - The current free-text search query
 * @param initialDataFetchingExecuted - Whether the initial data load has completed
 * @returns The current filter payload and its setter
 */
export const useExternalFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
) => {
  const orgId = getOrgIdForFetchExternalWorklist();

  const [filterPayload, setFilterPayload] =
    useState<SearchForMedicalPaOrdersFromPostgresParams | null>(null);

  const hasInitialFetchCompleted = useRef(false);

  useEffect(() => {
    if (initialDataFetchingExecuted && !hasInitialFetchCompleted.current) {
      hasInitialFetchCompleted.current = true;
    }

    if (!hasInitialFetchCompleted.current) return;

    const payload = generateFilterPayloadForExternalOrders(
      validFilters,
      searchTerm,
      orgId,
    );
    setFilterPayload(payload);
  }, [initialDataFetchingExecuted, validFilters, searchTerm, orgId]);

  return { filterPayload, setFilterPayload };
};
